'use client';

import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import { cx } from 'class-variance-authority';
import { LuArrowUpRight, LuBadgeCheck, LuEye, LuMapPin, LuTag, LuTruck } from 'react-icons/lu';
import type { IconType } from 'react-icons';
import { HoverTooltip } from './HoverTooltip';
import { venueCity } from '../utils/venueCity';
import { useI18n } from '~/features/i18n';
import { useVATRate } from '~/features/portals/money';
import { Units } from '~/modules/units';
import type { PartnerBadge, RankedPartner } from '../utils/partnerRanking';
import { LOCATION_SECTION_ANCHOR_ID } from '../types';
type Props = {
  ranked: RankedPartner;
  /** City the user searched from, for the "X km van {label}" line. */
  userLabel: string | null;
  /** Lightweight non-card row for the additional "other options". */
  minimal?: boolean;
  /**
   * Replaces the distance line with what this provider's trip costs. Named for its content
   * rather than its slot: the icon beside the line has to match the sentence, and a generic
   * "detail" left the transport amount sitting under a map pin.
   */
  transportLine?: string;
  /** True for the partner of the PDP being viewed — rendered as a plain (non-link) card. */
  isCurrent?: boolean;
  /** Notifies the parent which partner is hovered, so the map can emphasise its pin. */
  onHoverChange?: (activityId: number | null) => void;
  className?: string;
};
function badgeLabel(i18n: ReturnType<typeof useI18n>, badge: PartnerBadge): string {
  switch (badge) {
    case 'recommended':
      return i18n._('pdp.location.badge.recommended', undefined, {
        message: 'Recommended'
      });
    case 'closest':
      return i18n._('pdp.location.badge.closest', undefined, {
        message: 'Closest'
      });
    case 'cheapest':
      return i18n._('pdp.location.badge.cheapest', undefined, {
        message: 'Cheapest'
      });
    default:
      {
        const _exhaustive: never = badge;
        return _exhaustive;
      }
  }
}

/**
 * What each badge actually means. "Recommended" in particular read as a mystery ranking —
 * it is Funkey's preferred provider for this activity, and saying so is the difference
 * between a label the user trusts and one they have to guess at.
 */
function badgeExplanation(i18n: ReturnType<typeof useI18n>, badge: PartnerBadge): string {
  switch (badge) {
    case 'recommended':
      return i18n._('pdp.location.badge.recommended.explainer', undefined, {
        message: 'Our preferred provider for this activity.'
      });
    // prettier-ignore
    case 'closest':
      return i18n._('pdp.location.badge.closest.explainer', undefined, {
        message: 'Closest to the location you entered.'
      });
    // prettier-ignore
    case 'cheapest':
      return i18n._('pdp.location.badge.cheapest.explainer', undefined, {
        message: 'Lowest total for your group, transport included.'
      });
    // prettier-ignore
    default:
      {
        const _exhaustive: never = badge;
        return _exhaustive;
      }
  }
}

/**
 * One ranked partner offering, rendered partner-anonymously: city + distance +
 * "vanaf" price + badges. Links to the same activity for that partner via
 * `?id=` in a NEW TAB so the user can compare across tabs. Never renders the
 * partner name or exact address.
 */
export const PartnerResultCard: React.FC<Props> = t0 => {
  const $ = _c(77);
  const {
    ranked,
    userLabel,
    minimal,
    transportLine,
    isCurrent,
    onHoverChange,
    className
  } = t0;
  const i18n = useI18n();
  const vat = useVATRate();
  let t1;
  if ($[0] !== ranked.closestAddress || $[1] !== ranked.partner.partnerCity) {
    t1 = venueCity(ranked.closestAddress, ranked.partner.partnerCity);
    $[0] = ranked.closestAddress;
    $[1] = ranked.partner.partnerCity;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cityLabel = t1;
  let t2;
  if ($[3] !== ranked.cheapestPerPerson || $[4] !== vat) {
    t2 = ranked.cheapestPerPerson > 0 ? vat.calcEUR(ranked.cheapestPerPerson) : null;
    $[3] = ranked.cheapestPerPerson;
    $[4] = vat;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const price = t2;
  let t3;
  if ($[6] !== i18n || $[7] !== ranked.distanceKm || $[8] !== userLabel) {
    t3 = ranked.distanceKm != null ? userLabel ? i18n._("pdp.location.card.distance_from", {
      km: Math.round(ranked.distanceKm),
      city: userLabel
    }, {
      message: "\u2248 {km} km from {city}"
    }) : i18n._("pdp.location.card.distance", {
      km: Math.round(ranked.distanceKm)
    }, {
      message: "\u2248 {km} km"
    }) : null;
    $[6] = i18n;
    $[7] = ranked.distanceKm;
    $[8] = userLabel;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const distanceLabel = t3;
  let t4;
  if ($[10] !== i18n || $[11] !== price) {
    t4 = price != null && <p className="text-sm text-blue-900">{i18n._("pdp.location.card.from", undefined, {
        message: "from"
      })}{" "}<span className="font-semibold text-tertiary-600">{Units.formatEUR(price * 100)}</span><span className="ml-0.5 text-xs font-normal text-blue-900/60">{i18n._("pdp.location.card.per_person", undefined, {
          message: "p.p."
        })}</span></p>;
    $[10] = i18n;
    $[11] = price;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const priceLabel = t4;
  const t5 = `?id=${ranked.partner.activityId}#${LOCATION_SECTION_ANCHOR_ID}`;
  let t6;
  if ($[13] !== onHoverChange || $[14] !== ranked.partner.activityId) {
    t6 = () => onHoverChange?.(ranked.partner.activityId);
    $[13] = onHoverChange;
    $[14] = ranked.partner.activityId;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== onHoverChange) {
    t7 = () => onHoverChange?.(null);
    $[16] = onHoverChange;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== ranked.partner.activityId || $[19] !== t5 || $[20] !== t6 || $[21] !== t7) {
    t8 = {
      href: t5,
      target: "_blank",
      rel: "noopener noreferrer",
      "data-testid": "pdp-location-partner-card",
      "data-activity-id": ranked.partner.activityId,
      onMouseEnter: t6,
      onMouseLeave: t7
    };
    $[18] = ranked.partner.activityId;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const linkProps = t8 as const;
  if (minimal) {
    let t9;
    if ($[23] !== className) {
      t9 = cx("group flex items-center gap-3", className);
      $[23] = className;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== ranked.partner.imagePath) {
      t10 = ranked.partner.imagePath && <Image src={ranked.partner.imagePath} alt="" width={56} height={56} loading="lazy" className="size-14 shrink-0 rounded-lg object-cover" />;
      $[25] = ranked.partner.imagePath;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== cityLabel) {
      t11 = !!cityLabel && <p className="truncate text-sm font-medium text-blue-900">{cityLabel}</p>;
      $[27] = cityLabel;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== distanceLabel || $[30] !== transportLine) {
      t12 = !!(transportLine ?? distanceLabel) && <p className="text-xs text-blue-900/60">{transportLine ?? distanceLabel}</p>;
      $[29] = distanceLabel;
      $[30] = transportLine;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    let t13;
    if ($[32] !== priceLabel || $[33] !== t11 || $[34] !== t12) {
      t13 = <div className="flex min-w-0 flex-1 flex-col gap-0.5">{t11}{t12}{priceLabel}</div>;
      $[32] = priceLabel;
      $[33] = t11;
      $[34] = t12;
      $[35] = t13;
    } else {
      t13 = $[35];
    }
    let t14;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = <LuArrowUpRight className="size-4 shrink-0 text-blue-900/50 opacity-0 transition-opacity group-hover:opacity-100" />;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    let t15;
    if ($[37] !== linkProps || $[38] !== t10 || $[39] !== t13 || $[40] !== t9) {
      t15 = <a {...linkProps} className={t9}>{t10}{t13}{t14}</a>;
      $[37] = linkProps;
      $[38] = t10;
      $[39] = t13;
      $[40] = t9;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    return t15;
  }
  let t9;
  if ($[42] !== i18n || $[43] !== isCurrent || $[44] !== ranked.badges) {
    t9 = !!(isCurrent || ranked.badges.length) && <div className="flex flex-wrap items-center gap-1">{isCurrent && <span className={cx("inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-semibold leading-3", "bg-tertiary-600 text-white")}><LuEye className="size-3 shrink-0" /><span>{i18n._("pdp.location.badge.current", undefined, {
            message: "You're viewing this one"
          })}</span></span>}{ranked.badges.map(badge => {
        const Icon = BADGE_ICON[badge];
        return <HoverTooltip key={badge} content={badgeExplanation(i18n, badge)} position="top" triggerClassName="inline-flex"><span className={cx("inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-semibold leading-3", BADGE_STYLE[badge])}><Icon className="size-3 shrink-0" /><span>{badgeLabel(i18n, badge)}</span></span></HoverTooltip>;
      })}</div>;
    $[42] = i18n;
    $[43] = isCurrent;
    $[44] = ranked.badges;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  const badgeRow = t9;
  let t10;
  if ($[46] !== cityLabel) {
    t10 = !!cityLabel && <p className="text-sm font-semibold text-blue-900">{cityLabel}</p>;
    $[46] = cityLabel;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== distanceLabel || $[49] !== transportLine) {
    t11 = !!(transportLine ?? distanceLabel) && <p className="flex items-center gap-1 text-xs text-blue-900/60">{transportLine ? <LuTruck className="size-3.5 shrink-0" /> : <LuMapPin className="size-3.5 shrink-0" />}{transportLine ?? distanceLabel}</p>;
    $[48] = distanceLabel;
    $[49] = transportLine;
    $[50] = t11;
  } else {
    t11 = $[50];
  }
  let t12;
  if ($[51] !== badgeRow || $[52] !== priceLabel || $[53] !== t10 || $[54] !== t11) {
    t12 = <div className="flex flex-col gap-1">{t10}{badgeRow}{t11}{priceLabel}</div>;
    $[51] = badgeRow;
    $[52] = priceLabel;
    $[53] = t10;
    $[54] = t11;
    $[55] = t12;
  } else {
    t12 = $[55];
  }
  const body = t12;
  let t13;
  let t14;
  let t15;
  let thumbnail;
  if ($[56] !== body || $[57] !== className || $[58] !== isCurrent || $[59] !== linkProps || $[60] !== ranked.partner.imagePath) {
    t15 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const shellClassName = cx("flex items-center gap-3", className);
      let t16;
      if ($[65] !== ranked.partner.imagePath) {
        t16 = ranked.partner.imagePath ? <Image src={ranked.partner.imagePath} alt="" width={80} height={80} loading="lazy" className="size-20 shrink-0 rounded-xl object-cover" /> : null;
        $[65] = ranked.partner.imagePath;
        $[66] = t16;
      } else {
        t16 = $[66];
      }
      thumbnail = t16;
      if (isCurrent) {
        let t17;
        if ($[67] !== body) {
          t17 = <div className="min-w-0 flex-1">{body}</div>;
          $[67] = body;
          $[68] = t17;
        } else {
          t17 = $[68];
        }
        t15 = <div data-testid="pdp-location-partner-card" className={shellClassName}>{thumbnail}{t17}</div>;
        break bb0;
      }
      t13 = linkProps;
      t14 = cx("group", shellClassName);
    }
    $[56] = body;
    $[57] = className;
    $[58] = isCurrent;
    $[59] = linkProps;
    $[60] = ranked.partner.imagePath;
    $[61] = t13;
    $[62] = t14;
    $[63] = t15;
    $[64] = thumbnail;
  } else {
    t13 = $[61];
    t14 = $[62];
    t15 = $[63];
    thumbnail = $[64];
  }
  if (t15 !== Symbol.for("react.early_return_sentinel")) {
    return t15;
  }
  let t16;
  if ($[69] !== body) {
    t16 = <div className="min-w-0 flex-1">{body}</div>;
    $[69] = body;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <LuArrowUpRight className="size-4 shrink-0 text-blue-900/50 opacity-0 transition-opacity group-hover:opacity-100" />;
    $[71] = t17;
  } else {
    t17 = $[71];
  }
  let t18;
  if ($[72] !== t13 || $[73] !== t14 || $[74] !== t16 || $[75] !== thumbnail) {
    t18 = <a {...t13} className={t14}>{thumbnail}{t16}{t17}</a>;
    $[72] = t13;
    $[73] = t14;
    $[74] = t16;
    $[75] = thumbnail;
    $[76] = t18;
  } else {
    t18 = $[76];
  }
  return t18;
};
const BADGE_STYLE: Record<PartnerBadge, string> = {
  recommended: 'bg-blue-900 text-white',
  closest: 'bg-turqoise-500/15 text-turqoise-700',
  cheapest: 'bg-gray-100 text-blue-900/70'
};
const BADGE_ICON: Record<PartnerBadge, IconType> = {
  recommended: LuBadgeCheck,
  closest: LuMapPin,
  cheapest: LuTag
};